import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { EntitlementsService } from '@treasury/domain/entitlements';
import {
    Bank,
    CreditorsService,
    IntermediaryBank,
    WireConfiguration,
    WireCreditor,
    WireIsoService,
    WiresService,
} from '@treasury/domain/wires';
// eslint-disable-next-line no-duplicate-imports
import type { WireIsoLabels } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { deleteIcon, searchIcon } from '@treasury/presentation/assets/icons';
import { TmValueChanged, type SelectItemVm } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-bottom-sheet';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-segmented-control';
import '@treasury/presentation/components/tm-toggle-content';
import { debounce } from '@treasury/utils';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './creditor-banks-table';

const bankIdTypes = [
    {
        label: 'Swift Code',
        value: 'Swift Code',
    },
    {
        label: 'Sort Code',
        value: 'Sort Code',
    },
];

export const tagName = 'creditor-data-editable';
@customElement(tagName)
export class CreditorDataEditable extends TmContainer {
    @property({ type: Object })
    public wireConfiguration!: WireConfiguration;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @InjectProperty()
    declare private wiresService: WiresService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @DeepReactive()
    private creditor: WireCreditor = new WireCreditor();

    @state()
    private banks: Bank[] = [];

    @state()
    private states: SelectItemVm<string>[] = [];

    @state()
    private countries: SelectItemVm<string>[] = [];

    @state()
    private confirmAccountNumber: string | undefined;

    @state()
    private accountNumberConfirmed = true;

    @state()
    private banksBottomSheetOpen = false;

    @state()
    private bankSelectionType = 'creditor';

    @state()
    private selectedAgent = false;

    @state()
    private selectedAgentId: string | undefined;

    @state()
    private validAgentId = true;

    @state()
    private selectedInstructedAgentId: string | undefined;

    @state()
    private validInstructedAgentId = true;

    @state()
    private hasValidInstructedAgent = false;

    @state()
    private domesticCreateCreditorEntitlement = false;

    @state()
    private internationalCreateCreditorEntitlement = false;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private showOptionalCreditorInfo = false;

    @state()
    private locationBottomSheetOpen = false;

    @state()
    private addInstructedAgent = false;

    @state()
    private isExistingCreditor = false;

    private debouncedAgent = debounce(() => this.validateAgent(), 500);

    private debouncedInstructedAgent = debounce(() => this.validateInstructedAgent(), 500);

    private get internationalUsdWireEnabled() {
        return (
            this.domesticCreateCreditorEntitlement &&
            this.internationalCreateCreditorEntitlement &&
            this.wireConfiguration.companyWireProductEnabled &&
            this.wireConfiguration.companyInternationalUsdEnabled &&
            this.wireConfiguration.fiWireProductEnabled
        );
    }

    public connectedCallback(): void {
        super.connectedCallback();
        if (!this.creditor.isInternational) {
            this.creditor.country = 'US';
            if (this.creditor.bank) {
                this.creditor.bank.bankIdType = 'Routing Number';
                this.creditor.bank.country = 'US';
            }
        }
    }

    protected render() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return html`<tm-form
                @form-changed=${() => {
                    this.updateCreditor();
                }}
            >
                ${this.internationalUsdWireEnabled && !this.isExistingCreditor
                    ? this.renderCreditorTypeSelection()
                    : nothing}

                <tm-section class="p-4 border-t-4 border-b-4">
                    <div class="pb-2 font-medium">Creditor Agent Information</div>
                    ${this.creditor.isInternational
                        ? this.renderInternationalCreditorAgent()
                        : this.renderDomesticCreditorAgent()}
                </tm-section>
                <tm-section class="p-4 border-t-4 border-b-4">
                    <div class="pb-2 font-medium">Creditor Information</div>
                    ${this.renderCreditor()}
                </tm-section>
                <tm-section class="p-4 border-t-4 border-b-4">
                    <div class="flex justify-between">
                        <div class="pb-2 font-medium">
                            ${this.creditor.isInternational ? 'Intermediary' : 'Instructed'}
                            ${this.wireIsoLabels.agent} Information
                        </div>
                        ${this.addInstructedAgent && !this.creditor.isInternational
                            ? html`<button
                                  @click=${() => {
                                      this.creditor.resetIntermediaryBanks();
                                      this.selectedInstructedAgentId = undefined;
                                      this.addInstructedAgent = false;
                                      this.requestUpdate();
                                  }}
                              >
                                  <tm-icon
                                      .icon=${deleteIcon}
                                      color="var(--content-default)"
                                  ></tm-icon>
                              </button>`
                            : nothing}
                    </div>
                    ${this.creditor.isInternational
                        ? this.renderInternationalIntermediaryAgent()
                        : this.renderDomesticInstructedAgent()}
                </tm-section>
            </tm-form>
            ${this.renderBanksBottomSheet()} ${this.renderLocationBottomSheet()}`;
    }

    protected async firstUpdated() {
        this.isExistingCreditor = !!this.creditor.id;
        if (this.isExistingCreditor) {
            this.confirmAccountNumber = this.creditor.accountNumber;
        }
        if (this.creditor.isInternational && !this.creditor.intermediaryBanks?.length) {
            this.creditor.intermediaryBanks?.push(
                new IntermediaryBank({
                    bankIdType: 'Routing Number',
                    isInternational: false,
                } as unknown as IntermediaryBank)
            );
        }
        await this.tryFetch(
            async () =>
                Promise.all([
                    this.entitlementsService.getEntitlements(),
                    this.creditorsService.getBanks(),
                    this.creditorsService.getStates() as unknown as {
                        value: string;
                        key: string;
                    }[],
                    this.creditorsService.getCountries() as unknown as {
                        countryName: string;
                        countryCode: string;
                    }[],
                    this.wiresService.getWireConfiguration() as unknown as WireConfiguration,
                    this.wireIsoService.getLabels(),
                ]),
            ([entitlements, banks, states, countries, wireConfiguration, wireIsoLabels]) => {
                this.domesticCreateCreditorEntitlement =
                    entitlements.get('Wire, Domestic, Create Beneficiary') || false;
                this.internationalCreateCreditorEntitlement =
                    entitlements.get('Wire, International, Create Beneficiary') || false;
                this.banks = banks;
                this.states = states.map(state => ({
                    label: state.value,
                    value: state.key,
                }));
                this.states.unshift({ label: 'Select State', value: ' ' });
                this.countries = countries.map(country => ({
                    label: country.countryName,
                    value: country.countryCode,
                }));
                this.countries.unshift({ label: 'Select Country', value: ' ' });
                this.wireConfiguration = wireConfiguration;
                this.wireIsoLabels = wireIsoLabels;
            }
        );

        if (this.creditor.intermediaryBanks && this.creditor.intermediaryBanks[0]) {
            this.selectedInstructedAgentId = this.creditor.intermediaryBanks[0].bankId;
            this.addInstructedAgent = true;
        }
    }

    protected updated() {
        this.updateCreditor();
    }

    private updateCreditor() {
        this.dispatchEvent(
            new CustomEvent('update', {
                detail: {
                    creditor: this.creditor,
                    hasValidInstructedAgent: this.hasValidInstructedAgent,
                    validInstructedAgentState: this.validInstructedAgentId,
                },
            })
        );
    }

    private validateAgent() {
        const bankId = this.creditor.bank?.bankId;
        if (bankId) {
            this.validAgentId = this.creditorsService.validateAgentId(bankId);
        }
        if (this.creditor.bank) {
            if (this.validAgentId) {
                const bank = this.banks.find(bank => bank.bankId === bankId);
                if (bank) {
                    this.creditor.bank.name = bank?.name;
                    this.creditor.bank.city = bank?.city;
                    this.creditor.bank.state = bank?.state;
                    this.selectedAgent = true;
                    this.selectedAgentId = bank?.bankId;
                }
            } else {
                if (this.selectedAgent) {
                    this.creditor.bank.name = '';
                    this.creditor.bank.city = '';
                    this.creditor.bank.state = ' ';
                }
                this.selectedAgent = false;
            }
        }
    }

    private validateInstructedAgent() {
        const bankId = this.selectedInstructedAgentId;
        this.validInstructedAgentId = bankId
            ? this.creditorsService.validateAgentId(bankId)
            : false;

        const bank = this.banks.find(bank => bank.bankId === bankId);
        const currentBank = this.creditor.intermediaryBanks?.[0];
        const { accountNumber, notes } = currentBank || {};
        this.creditor.intermediaryBanks?.shift();
        const { intermediaryBanks } = this.creditor;
        this.creditor.resetIntermediaryBanks();
        if (this.validInstructedAgentId && bank) {
            this.selectedInstructedAgentId = bank.bankId;
            this.creditor.intermediaryBanks?.push(
                new IntermediaryBank({
                    id: bank.id,
                    bankId: bank.bankId,
                    name: bank.name,
                    city: bank.city,
                    state: bank.state,
                    country: bank.country,
                    accountNumber,
                    notes,
                } as IntermediaryBank)
            );
            this.hasValidInstructedAgent = true;
        } else {
            if (this.creditor.isInternational) {
                this.creditor.intermediaryBanks?.push(
                    new IntermediaryBank({
                        id: '',
                        bankId: '',
                        name: '',
                        city: '',
                        state: '',
                        country: '',
                        bankIdType: 'Routing Number',
                        isInternational: false,
                    } as unknown as IntermediaryBank)
                );
            }
            this.hasValidInstructedAgent = false;
            if (!bankId) {
                this.validInstructedAgentId = true;
                this.updateCreditor();
            }
        }
        intermediaryBanks?.map(bank => this.creditor.intermediaryBanks?.push(bank));
    }

    private renderValidationMessage() {
        const message = this.hasValidInstructedAgent
            ? 'Please ensure the agent ID is correct before initiating the wire.'
            : 'Agent ID must be a valid routing number.';
        return !this.validAgentId
            ? html`<div class="flex justify-end text-[13px] text-secondary text-right">
                  ${message}
              </div>`
            : nothing;
    }

    private renderCreditorTypeSelection() {
        const creditorTypes = [
            {
                label: 'Domestic',
                value: false,
            },
            {
                label: 'International USD',
                value: true,
            },
        ];
        const initial = creditorTypes.find(t => t.value === this.creditor.isInternational);
        return html`<tm-section class="p-4">
            <tm-segmented-control
                class="py-2"
                .controlTheme=${'secondary'}
                .options=${creditorTypes}
                .initial=${initial}
                @value-changed=${({ detail }: TmValueChanged<SelectItemVm<boolean>>) => {
                    const value = detail.value?.value ?? false;
                    if (value === this.creditor.isInternational) {
                        return;
                    }
                    this.creditor = new WireCreditor();
                    this.creditor.isInternational = value;
                    if (this.creditor.isInternational) {
                        this.creditor.country = ' ';
                        if (this.creditor.bank) {
                            this.creditor.bank.bankIdType = 'Swift Code';
                            this.creditor.bank.isInternational = true;
                        }
                        this.selectedInstructedAgentId = '';
                        this.creditor.intermediaryBanks?.push(
                            new IntermediaryBank({
                                bankIdType: 'Routing Number',
                                isInternational: false,
                            } as unknown as IntermediaryBank)
                        );
                    } else {
                        this.creditor.country = 'US';
                        if (this.creditor.bank) {
                            this.creditor.bank.bankIdType = 'Routing Number';
                            this.creditor.bank.country = 'US';
                        }
                    }
                    this.confirmAccountNumber = undefined;
                    this.accountNumberConfirmed = true;
                    this.requestUpdate();
                }}
            ></tm-segmented-control>
        </tm-section> `;
    }

    private renderDomesticCreditorAgent() {
        return html`
            <tm-text-field
                label=${this.wireIsoLabels.agentId}
                .initial=${this.creditor.bank?.bankId}
                .maxLength=${9}
                .invalid=${!this.validAgentId}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.bankId = e.detail.value;
                        this.selectedAgentId = this.creditor.bank.bankId;
                        if (!this.creditor.isInternational) {
                            this.debouncedAgent();
                        }
                    }
                }}
            >
                <button
                    title="Search"
                    slot="suffix"
                    @click=${() => {
                        this.bankSelectionType = 'creditor';
                        this.banksBottomSheetOpen = true;
                    }}
                >
                    <tm-icon .icon=${searchIcon} color="var(--content-default)"></tm-icon>
                </button>
            </tm-text-field>
            ${this.renderValidationMessage()}
            <tm-text-field
                label="${this.wireIsoLabels.agent} Name"
                .initial=${this.creditor.bank?.name}
                .readonly=${this.creditor.isInternational ? false : this.selectedAgent}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.name = e.detail.value;
                    }
                }}
            ></tm-text-field>
            <tm-text-field
                label="${this.wireIsoLabels.agent} ${this.wireIsoLabels.cityTownName}"
                .initial=${this.creditor.bank?.city}
                .readonly=${this.creditor.isInternational ? false : this.selectedAgent}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.city = e.detail.value;
                    }
                }}
            ></tm-text-field>
            ${this.selectedAgent
                ? html`<tm-text-field
                      label="${this.wireIsoLabels.agent} ${this.wireIsoLabels
                          .stateCountrySubDivision}"
                      .initial=${this.creditor.bank?.state}
                      .maxLength=${35}
                      .readonly=${this.selectedAgent && !this.creditor.isInternational}
                      @value-changed=${(e: CustomEvent) => {
                          if (this.creditor.bank) {
                              this.creditor.bank.state = e.detail.value;
                          }
                      }}
                  ></tm-text-field>`
                : html`<tm-select
                      label="${this.wireIsoLabels.agent} ${this.wireIsoLabels
                          .stateCountrySubDivision}"
                      .initial=${this.creditor.bank?.state}
                      .items=${this.states}
                      .readonly=${this.selectedAgent}
                      @value-changed=${(e: CustomEvent) => {
                          if (this.creditor.bank) {
                              this.creditor.bank.state = e.detail.value;
                          }
                      }}
                  ></tm-select>`}
            <tm-text-field
                label="${this.wireIsoLabels.agent} Country"
                .initial=${'US'}
                .readonly=${true}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.country = e.detail.value;
                    }
                }}
            ></tm-text-field>
        `;
    }

    private renderInternationalCreditorAgent() {
        const initialBankIdType = bankIdTypes.find(t => t.value === this.creditor.bank?.bankIdType);
        if (this.creditor.bank) {
            this.creditor.bank.isInternational = true;
        }
        return html`
            <tm-text-field
                label=${this.wireIsoLabels.agentId}
                .initial=${this.creditor.bank?.bankId}
                .maxLength=${9}
                .invalid=${!this.validAgentId}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.bankId = e.detail.value;
                        this.selectedAgentId = this.creditor.bank.bankId;
                        if (!this.creditor.isInternational) {
                            this.debouncedAgent();
                        }
                    }
                }}
            >
            </tm-text-field>
            <tm-segmented-control
                class="py-2"
                .controlTheme=${'secondary'}
                .initial=${initialBankIdType}
                .options=${bankIdTypes}
                @value-changed=${({ detail }: TmValueChanged<SelectItemVm<string>>) => {
                    if (!detail.value) {
                        return;
                    }
                    const { value } = detail.value;
                    if (this.creditor.bank) {
                        this.creditor.bank.bankIdType = value;
                    }
                }}
            ></tm-segmented-control>

            <tm-text-field
                label="${this.wireIsoLabels.agent} Name"
                .initial=${this.creditor.bank?.name}
                .readonly=${this.creditor.isInternational ? false : this.selectedAgent}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.name = e.detail.value;
                    }
                }}
            ></tm-text-field>
            <tm-select
                label="${this.wireIsoLabels.agent} Country"
                .initial=${this.creditor.bank?.country}
                .items=${this.countries}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.country = e.detail.value;
                    }
                }}
            ></tm-select>
            <tm-text-field
                label="${this.wireIsoLabels.agent} ${this.wireIsoLabels.cityTownName}"
                .initial=${this.creditor.bank?.city}
                .readonly=${this.creditor.isInternational ? false : this.selectedAgent}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.bank) {
                        this.creditor.bank.city = e.detail.value;
                    }
                }}
            ></tm-text-field>
            <tm-toggle-content>
                <tm-text-field
                    label="${this.wireIsoLabels.agent} ${this.wireIsoLabels
                        .stateCountrySubDivision}"
                    .initial=${this.creditor.bank?.state}
                    .maxLength=${35}
                    .readonly=${this.selectedAgent && !this.creditor.isInternational}
                    @value-changed=${(e: CustomEvent) => {
                        if (this.creditor.bank) {
                            this.creditor.bank.state = e.detail.value;
                        }
                    }}
                ></tm-text-field>
            </tm-toggle-content>
        `;
    }

    private renderCreditor() {
        return html`${this.creditor.isInternational
                ? html`<tm-text-field
                      label="Currency"
                      .initial=${'USD'}
                      .readonly=${true}
                  ></tm-text-field>`
                : nothing}
            <tm-text-field
                label=${this.creditor.isInternational ? 'Account/IBAN Number' : 'Account Number'}
                .initial=${this.creditor.accountNumber}
                .maxLength=${35}
                .allowedCharacters=${'[a-zA-Z0-9]'}
                @value-changed=${(e: CustomEvent) => {
                    this.creditor.accountNumber = e.detail.value;
                    if (this.confirmAccountNumber) {
                        this.accountNumberConfirmed =
                            this.confirmAccountNumber === this.creditor.accountNumber;
                        this.dispatchEvent(
                            new CustomEvent('account-match', {
                                detail: this.accountNumberConfirmed,
                            })
                        );
                    }
                }}
            ></tm-text-field>
            <tm-text-field
                label=${this.creditor.isInternational
                    ? 'Re-enter Account/IBAN Number'
                    : 'Re-enter Account Number'}
                .initial=${this.confirmAccountNumber}
                .maxLength=${35}
                .invalid=${!this.accountNumberConfirmed}
                .helperText=${!this.accountNumberConfirmed ? 'Account number does not match.' : ''}
                .allowedCharacters=${'[a-zA-Z0-9]'}
                @value-changed=${(e: CustomEvent) => {
                    if (!e.detail.value) {
                        return;
                    }
                    this.confirmAccountNumber = e.detail.value;
                    this.accountNumberConfirmed =
                        this.confirmAccountNumber === this.creditor.accountNumber;
                    this.dispatchEvent(
                        new CustomEvent('account-match', {
                            detail: this.accountNumberConfirmed,
                        })
                    );
                }}
            ></tm-text-field>
            <tm-text-field
                label="Name"
                .initial=${this.creditor.name}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    this.creditor.name = e.detail.value;
                }}
            ></tm-text-field>
            <tm-select
                label="Country"
                .items=${this.countries}
                .initial=${this.creditor.country}
                @value-changed=${(e: CustomEvent) => {
                    this.creditor.country = e.detail.value;
                }}
            ></tm-select>

            ${this.showOptionalCreditorInfo
                ? html` <tm-text-field
                          label="Building Number"
                          .initial=${this.creditor.buildingNumber}
                          .maxLength=${16}
                          @value-changed=${(e: CustomEvent) => {
                              this.creditor.buildingNumber = e.detail.value;
                          }}
                      ></tm-text-field>
                      <tm-text-field
                          label="Street Name"
                          .initial=${this.creditor.streetName}
                          .maxLength=${70}
                          @value-changed=${(e: CustomEvent) => {
                              this.creditor.streetName = e.detail.value;
                          }}
                      ></tm-text-field>`
                : nothing}

            <tm-text-field
                label="City/Town Name"
                .initial=${this.creditor.city}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    this.creditor.city = e.detail.value;
                }}
            ></tm-text-field>

            ${this.showOptionalCreditorInfo
                ? html`<tm-text-field
                          label="State/Country Sub Division"
                          .initial=${this.creditor.state}
                          .maxLength=${35}
                          @value-changed=${(e: CustomEvent) => {
                              this.creditor.state = e.detail.value;
                          }}
                      ></tm-text-field>
                      <tm-text-field
                          label="Post Code"
                          .initial=${this.creditor.postalCode}
                          .maxLength=${5}
                          @value-changed=${(e: CustomEvent) => {
                              this.creditor.postalCode = e.detail.value;
                          }}
                      ></tm-text-field>
                      <tm-button
                          importance="secondary"
                          @click=${() => {
                              this.locationBottomSheetOpen = true;
                          }}
                          >Additional location information</tm-button
                      >

                      <tm-text-field
                          label="Notes"
                          .initial=${this.creditor.notes}
                          .maxLength=${35}
                          @value-changed=${(e: CustomEvent) => {
                              this.creditor.notes = e.detail.value;
                          }}
                      ></tm-text-field>`
                : nothing}

            <tm-button
                importance="tertiary"
                @click=${() => {
                    this.showOptionalCreditorInfo = !this.showOptionalCreditorInfo;
                }}
                >${this.showOptionalCreditorInfo ? 'Hide' : 'Show'} options</tm-button
            >`;
    }

    private renderDomesticInstructedAgent() {
        if (this.creditor.isInternational) {
            return nothing;
        }
        const bank = this.creditor.intermediaryBanks?.[0]
            ? this.creditor.intermediaryBanks[0]
            : new IntermediaryBank();

        return html`${this.addInstructedAgent
            ? html`<tm-text-field
                      label="Country"
                      .initial=${'US'}
                      .readonly=${true}
                  ></tm-text-field>
                  ${this.renderIntermediaryBank(bank as IntermediaryBank, 0)}`
            : html`<tm-button
                  importance="secondary"
                  @click=${() => {
                      this.addInstructedAgent = true;
                      this.creditor.intermediaryBanks?.push(bank);
                  }}
                  >Add an Instructed Agent</tm-button
              >`}`;
    }

    private renderInternationalIntermediaryAgent() {
        if (!this.creditor.isInternational) {
            return nothing;
        }

        return html` ${this.creditor.intermediaryBanks?.map((bank, i) => {
                return html`<div class="py-4 border-t-2">
                    <div class="flex justify-between">
                        <div class="text-sm font-medium">
                            ${bank.isInternational ? `International` : `Domestic `} Intermediary
                            ${this.wireIsoLabels.agent}
                        </div>
                        ${bank.isInternational
                            ? html`<button
                                  @click=${() => {
                                      this.creditor.intermediaryBanks?.splice(i, 1);
                                      this.requestUpdate();
                                  }}
                              >
                                  <tm-icon
                                      .icon=${deleteIcon}
                                      color="var(--content-default)"
                                  ></tm-icon>
                              </button>`
                            : nothing}
                    </div>

                    ${this.renderIntermediaryBank(bank as IntermediaryBank, i)}
                </div>`;
            })}

            <tm-button
                importance="secondary"
                @click=${() => {
                    this.creditor.intermediaryBanks?.push(
                        new IntermediaryBank({
                            bankIdType: 'Swift Code',
                            isInternational: true,
                        } as unknown as IntermediaryBank)
                    );
                    this.requestUpdate();
                }}
                >Add an ${this.wireIsoLabels.internationalIntermediaryAgent}</tm-button
            >`;
    }

    private renderIntermediaryBank(bank: IntermediaryBank | undefined, i: number) {
        if (!bank) {
            bank = new IntermediaryBank();
        }
        const initialBankIdType = bankIdTypes.find(t => t.value === bank.bankIdType);
        return html`<tm-text-field
                label="${this.wireIsoLabels.agentId}"
                .initial=${bank.isInternational ? bank.bankId : this.selectedInstructedAgentId}
                .maxLength=${9}
                .invalid=${bank.isInternational ? false : !this.validInstructedAgentId}
                @value-changed=${({ detail }: CustomEvent) => {
                    if (bank.isInternational) {
                        if (this.creditor.intermediaryBanks) {
                            this.creditor.intermediaryBanks[i].bankId = detail.value;
                        }
                    } else {
                        this.selectedInstructedAgentId = detail.value;
                        this.debouncedInstructedAgent();
                    }
                }}
            >
                ${bank.isInternational
                    ? nothing
                    : html` <div slot="suffix">
                          <button
                              title="Search"
                              @click=${() => {
                                  this.bankSelectionType = 'intermediary';
                                  this.banksBottomSheetOpen = true;
                              }}
                          >
                              <tm-icon .icon=${searchIcon} color="var(--content-default)"></tm-icon>
                          </button>
                          ${!this.creditor.isInternational && !this.validInstructedAgentId
                              ? html`<button
                                    title="Delete"
                                    @click=${() => {
                                        this.selectedInstructedAgentId = '';
                                    }}
                                >
                                    <tm-icon
                                        .icon=${deleteIcon}
                                        color="var(--content-default)"
                                    ></tm-icon>
                                </button>`
                              : nothing}
                      </div>`}
            </tm-text-field>

            ${bank.isInternational
                ? html`<tm-segmented-control
                      class="py-2"
                      .controlTheme=${'secondary'}
                      .options=${bankIdTypes}
                      .initial=${initialBankIdType}
                      @value-changed=${({ detail }: TmValueChanged<SelectItemVm<string>>) => {
                          if (!detail.value) {
                              return;
                          }
                          const { value } = detail.value;
                          if (this.creditor.intermediaryBanks) {
                              this.creditor.intermediaryBanks[i].bankIdType = value;
                          }
                      }}
                  ></tm-segmented-control>`
                : nothing}
            ${this.creditor.isInternational && !bank.isInternational
                ? html`<tm-text-field
                      label="Account Number"
                      .initial=${bank.accountNumber}
                      @value-changed=${(e: CustomEvent) => {
                          if (this.creditor.intermediaryBanks) {
                              this.creditor.intermediaryBanks[i].accountNumber = e.detail.value;
                          }
                      }}
                  ></tm-text-field>`
                : nothing}

            <tm-text-field
                label="${this.wireIsoLabels.agent} Name"
                .initial=${bank.name ?? ''}
                .readonly=${!bank.isInternational}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.isInternational && this.creditor.intermediaryBanks) {
                        this.creditor.intermediaryBanks[i].name = e.detail.value;
                    }
                }}
            ></tm-text-field>
            <tm-text-field
                label="${this.wireIsoLabels.agent} ${this.wireIsoLabels.cityTownName}"
                .initial=${bank.city ?? ''}
                .readonly=${!bank.isInternational}
                .maxLength=${35}
                @value-changed=${(e: CustomEvent) => {
                    if (this.creditor.isInternational && this.creditor.intermediaryBanks) {
                        this.creditor.intermediaryBanks[i].city = e.detail.value;
                    }
                }}
            ></tm-text-field>

            ${bank.isInternational
                ? html` <tm-select
                      label="${this.wireIsoLabels.agent} Country"
                      .items=${this.countries}
                      .initial=${bank.country ?? ''}
                      @value-changed=${(e: CustomEvent) => {
                          if (this.creditor.intermediaryBanks) {
                              this.creditor.intermediaryBanks[i].country = e.detail.value;
                          }
                      }}
                  ></tm-select>`
                : html`<tm-text-field
                      label="${this.wireIsoLabels.agent} ${this.wireIsoLabels
                          .stateCountrySubDivision}"
                      .initial=${bank.state ?? ''}
                      .readonly=${true}
                  ></tm-text-field>`}
            ${this.creditor.isInternational
                ? html`<tm-text-field
                      label="Notes"
                      .initial=${bank.notes}
                      .maxLength=${35}
                      @value-changed=${(e: CustomEvent) => {
                          if (this.creditor.intermediaryBanks) {
                              this.creditor.intermediaryBanks[i].notes = e.detail.value;
                          }
                      }}
                  ></tm-text-field>`
                : nothing} `;
    }

    private renderBanksBottomSheet() {
        return html`<tm-bottom-sheet
            .open=${this.banksBottomSheetOpen}
            @close=${() => {
                this.banksBottomSheetOpen = false;
            }}
        >
            <p slot="header-center">Bank Lookup</p>
            <creditor-banks-table
                .banks=${this.banks}
                .selectedAgentId=${this.selectedAgentId}
                .selectedInstructedAgentId=${this.selectedInstructedAgentId}
                @select-bank=${({ detail }: CustomEvent) => {
                    if (detail) {
                        if (this.creditor.bank && this.bankSelectionType === 'creditor') {
                            this.selectedAgent = true;
                            this.creditor.bank.id = detail.id;
                            this.creditor.bank.bankId = detail.bankId;
                            this.creditor.bank.name = detail.name;
                            this.creditor.bank.city = detail.city;
                            this.creditor.bank.state = detail.state;
                        }
                        if (this.bankSelectionType === 'intermediary') {
                            this.selectedInstructedAgentId = detail.bankId;
                            this.validateInstructedAgent();
                        }
                    }
                }}
            ></creditor-banks-table>
        </tm-bottom-sheet>`;
    }

    private renderLocationBottomSheet() {
        return html`<tm-bottom-sheet
            .open=${this.locationBottomSheetOpen}
            @close=${() => {
                this.locationBottomSheetOpen = false;
            }}
        >
            <span slot="header-center">Additional location information</span>
            <tm-section class="p-3 mb-10">
                <tm-text-field
                    label="Department"
                    .initial=${this.creditor.department}
                    .maxLength=${70}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.department = e.detail.value;
                    }}
                ></tm-text-field>
                <tm-text-field
                    label="Sub Department"
                    .initial=${this.creditor.subDepartment}
                    .maxLength=${70}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.subDepartment = e.detail.value;
                    }}
                ></tm-text-field>
                <tm-text-field
                    label="Post Box"
                    .initial=${this.creditor.postBox}
                    .maxLength=${16}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.postBox = e.detail.value;
                    }}
                ></tm-text-field>
                <tm-text-field
                    label="Building Name"
                    .initial=${this.creditor.buildingName}
                    .maxLength=${35}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.buildingName = e.detail.value;
                    }}
                ></tm-text-field>
                <tm-text-field
                    label="Floor"
                    .initial=${this.creditor.buildingFloor}
                    .maxLength=${70}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.buildingFloor = e.detail.value;
                    }}
                ></tm-text-field>
                <tm-text-field
                    label="Room"
                    .initial=${this.creditor.buildingRoom}
                    .maxLength=${70}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.buildingRoom = e.detail.value;
                    }}
                ></tm-text-field>
                <tm-text-field
                    label="Town Location Name"
                    .initial=${this.creditor.townLocationName}
                    .maxLength=${35}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.townLocationName = e.detail.value;
                    }}
                ></tm-text-field>
                <tm-text-field
                    label="District Name"
                    .initial=${this.creditor.districtName}
                    .maxLength=${35}
                    @value-changed=${(e: CustomEvent) => {
                        this.creditor.districtName = e.detail.value;
                    }}
                ></tm-text-field>
            </tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Done',
                        onClick: () => {
                            this.locationBottomSheetOpen = false;
                        },
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>
        </tm-bottom-sheet>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CreditorDataEditable;
    }
}
